import { v4 } from 'uuid';

const MENTION_TOKEN_PATTERN = /@\[mention:([0-9a-fA-F-]{36})\]/g;

export type MessageMentionBinding = {
  id: string;
  fallback_label: string;
  person_id?: string | null;
  person?: { id: string; name?: string | null } | null;
};

export type MessageMentionInsert = {
  id: string;
  person_id: string;
  fallback_label: string;
};

export type ResolvedMessageSegment =
  | { type: 'text'; key: string; text: string }
  | {
      type: 'mention';
      key: string;
      mentionId: string;
      label: string;
    };

type SerializedEditorMessage = {
  content: string;
  contentTemplate: string | null;
  mentions: MessageMentionInsert[];
};

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

function decodeHtmlEntities(value: string) {
  return value.replace(
    /&(#(?:x[0-9a-f]+|\d+)|amp|apos|gt|lt|nbsp|quot);/gi,
    (entity, entityName: string) => {
      if (entityName.startsWith('#x') || entityName.startsWith('#X')) {
        const codePoint = Number.parseInt(entityName.slice(2), 16);
        return Number.isFinite(codePoint)
          ? String.fromCodePoint(codePoint)
          : entity;
      }

      if (entityName.startsWith('#')) {
        const codePoint = Number.parseInt(entityName.slice(1), 10);
        return Number.isFinite(codePoint)
          ? String.fromCodePoint(codePoint)
          : entity;
      }

      return NAMED_ENTITIES[entityName.toLocaleLowerCase()] ?? entity;
    },
  );
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getHtmlAttributes(tag: string) {
  const attributes = new Map<string, string>();
  const attributePattern = /([^\s=/>]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;

  for (const match of tag.matchAll(attributePattern)) {
    const name = match[1]?.toLocaleLowerCase();
    const value = match[2] ?? match[3];
    if (name && value !== undefined) {
      attributes.set(name, decodeHtmlEntities(value));
    }
  }

  return attributes;
}

function appendNewline(parts: string[]) {
  if (parts.length && !parts[parts.length - 1]?.endsWith('\n')) {
    parts.push('\n');
  }
}

export function serializeMessageEditorValue({
  html,
  text,
}: {
  html: string;
  text: string;
}): SerializedEditorMessage {
  const templateParts: string[] = [];
  const mentions: MessageMentionInsert[] = [];
  const seenMentionIds = new Set<string>();
  let activeMentionId: string | null = null;
  const htmlPartPattern = /<[^>]*>|[^<]+|</g;

  for (const match of html.matchAll(htmlPartPattern)) {
    const part = match[0];

    if (!part.startsWith('<')) {
      const htmlText = decodeHtmlEntities(part);

      if (/^[\t\r\n ]+$/.test(htmlText) && /[\r\n]/.test(htmlText)) {
        continue;
      }

      if (!activeMentionId) {
        templateParts.push(htmlText);
      }
      continue;
    }

    if (/^<mention\b/i.test(part)) {
      const attributes = getHtmlAttributes(part);
      const personId = attributes.get('id') ?? attributes.get('data-person-id');
      const fallbackLabel =
        attributes.get('label') ??
        attributes.get('data-fallback-label') ??
        attributes.get('text')?.replace(/^@/, '');
      const existingMentionId =
        attributes.get('data-binding') ??
        attributes.get('binding') ??
        attributes.get('data-mention-id');
      const mentionId =
        existingMentionId && !seenMentionIds.has(existingMentionId)
          ? existingMentionId
          : v4();

      if (
        mentionId &&
        personId &&
        fallbackLabel &&
        !seenMentionIds.has(mentionId)
      ) {
        activeMentionId = mentionId;
        seenMentionIds.add(mentionId);
        templateParts.push(`@[mention:${mentionId}]`);
        mentions.push({
          id: mentionId,
          person_id: personId,
          fallback_label: fallbackLabel,
        });
      }
      continue;
    }

    if (/^<\/mention\s*>/i.test(part)) {
      activeMentionId = null;
      continue;
    }

    if (/^<br\b/i.test(part)) {
      if (!activeMentionId) {
        templateParts.push('\n');
      }
      continue;
    }

    if (/^<\/(?:p|div|li|h[1-6]|blockquote)\s*>/i.test(part)) {
      if (!activeMentionId) {
        appendNewline(templateParts);
      }
    }
  }

  const contentTemplate = templateParts.join('').trim();

  return {
    content: text,
    contentTemplate: mentions.length ? contentTemplate : null,
    mentions,
  };
}

function getMentionLabel(binding: MessageMentionBinding) {
  return binding.person?.name || binding.fallback_label;
}

export function resolveMessage(
  content: string | null | undefined,
  contentTemplate: string | null | undefined,
  bindings: readonly MessageMentionBinding[] | null | undefined,
) {
  const fallbackContent = content ?? '';
  const fallback = {
    plainText: fallbackContent,
    segments: fallbackContent
      ? ([
          { type: 'text', key: 'text:0', text: fallbackContent },
        ] satisfies ResolvedMessageSegment[])
      : [],
  };
  if (!contentTemplate) return fallback;

  const bindingsById = new Map(
    (bindings ?? []).map((binding) => [binding.id, binding]),
  );
  const segments: ResolvedMessageSegment[] = [];
  let lastIndex = 0;

  for (const match of contentTemplate.matchAll(MENTION_TOKEN_PATTERN)) {
    const mentionId = match[1];
    const index = match.index;
    const binding = mentionId ? bindingsById.get(mentionId) : undefined;

    if (!binding || index === undefined) {
      return fallback;
    }

    if (index > lastIndex) {
      segments.push({
        type: 'text',
        key: `text:${lastIndex}:${index}`,
        text: contentTemplate.slice(lastIndex, index),
      });
    }

    segments.push({
      type: 'mention',
      key: `mention:${mentionId}`,
      mentionId,
      label: `@${getMentionLabel(binding)}`,
    });
    lastIndex = index + match[0].length;
  }

  if (lastIndex < contentTemplate.length) {
    segments.push({
      type: 'text',
      key: `text:${lastIndex}:end`,
      text: contentTemplate.slice(lastIndex),
    });
  }

  return {
    plainText: segments
      .map((segment) =>
        segment.type === 'mention' ? segment.label : segment.text,
      )
      .join(''),
    segments,
  };
}

export function getMessageEditorHtml(
  content: string | null | undefined,
  contentTemplate: string | null | undefined,
  bindings: readonly MessageMentionBinding[] | null | undefined,
) {
  const resolved = resolveMessage(content, contentTemplate, bindings);

  const inlineHtml = resolved.segments
    .map((segment, segmentIndex) => {
      if (segment.type === 'text') {
        return escapeHtml(segment.text);
      }

      const binding = bindings?.find(
        (candidate) => candidate.id === segment.mentionId,
      );
      if (!binding?.person_id) {
        return escapeHtml(segment.label);
      }

      const editorSeparator =
        segmentIndex === resolved.segments.length - 1 ? ' ' : '';

      return `<mention indicator="@" text="${escapeHtml(segment.label)}" id="${escapeHtml(binding.person_id)}" data-fallback-label="${escapeHtml(binding.fallback_label)}" data-binding="${escapeHtml(binding.id)}">${escapeHtml(segment.label)}</mention>${editorSeparator}`;
    })
    .join('');
  const blocks = inlineHtml
    .split('\n')
    .map((line) => (line ? `<p>${line}</p>` : '<br>'))
    .join('\n');

  return `<html>\n${blocks}\n</html>`;
}
